import { useState, useEffect, useCallback, useRef } from 'react'
import { Table } from '@tanstack/react-table'

interface UseTableRegroupingProps<TData> {
  groupedData: TData[]
  defaultExpanded: boolean
  tableRef: React.MutableRefObject<Table<TData> | null>
}

export function useTableRegrouping<TData>({ groupedData, defaultExpanded, tableRef }: UseTableRegroupingProps<TData>) {
  // Local state for drag-and-drop regrouping
  const [localData, setLocalData] = useState<TData[]>(groupedData)

  // Track expanded state for controlled expansion
  const [expandedState, setExpandedState] = useState<Record<string, boolean>>({})
  const expandedStateRef = useRef<Record<string, boolean>>({})

  // Sync localData with groupedData when props change
  useEffect(() => {
    setLocalData(groupedData)
  }, [groupedData])

  const handleExpandedChange = useCallback((updater: unknown) => {
    setExpandedState((prev) => {
      const newValue = typeof updater === 'function' ? updater(prev) : updater
      // Handle both true (all expanded) and Record<string, boolean> cases
      let result: Record<string, boolean>
      if (newValue === true) {
        result = {}
      } else {
        result = newValue as Record<string, boolean>
      }
      expandedStateRef.current = result
      return result
    })
  }, [])

  // Handler to ungroup a subrow (move it back to top-level)
  const handleUngroup = useCallback((rowId: string) => {
    setLocalData((prevData) => {
      const newData = [...prevData]
      let foundRow: TData | null = null
      let parentGroupIndex = -1

      // Find the row and its parent group
      for (let i = 0; i < newData.length; i++) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const item = newData[i] as any
        if (item.isGroup && item.subRows) {
          const subRowIndex = item.subRows.findIndex(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (subRow: any) => String(subRow.id) === String(rowId)
          )
          if (subRowIndex !== -1) {
            foundRow = item.subRows[subRowIndex]
            parentGroupIndex = i
            break
          }
        }
      }

      if (!foundRow || parentGroupIndex === -1) return prevData

      // Remove the row from its parent group's subRows
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const parentGroup = { ...newData[parentGroupIndex] } as any
      parentGroup.subRows = parentGroup.subRows.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (subRow: any) => String(subRow.id) !== String(rowId)
      )

      // If the group has no more subrows, keep it
      // REMOVED: if (parentGroup.subRows.length === 0) { newData.splice(parentGroupIndex, 1) }
      newData[parentGroupIndex] = parentGroup

      // Add the ungrouped row to top-level
      newData.push(foundRow)

      return newData
    })
  }, [])

  // Handler to update a group row's data locally
  const handleUpdateGroup = useCallback((groupId: string, data: TData) => {
    setLocalData((prevData) => {
      const newData = [...prevData]
      const index = newData.findIndex((item: TData) => String((item as Record<string, unknown>).id) === String(groupId))
      if (index !== -1) {
        newData[index] = { ...newData[index], ...data }
      }
      return newData
    })
  }, [])

  // Handler to delete a group row locally and orphan its subrows
  const handleDeleteGroup = useCallback((groupId: string) => {
    setLocalData((prevData) => {
      const newData = [...prevData]
      const index = newData.findIndex((item: TData) => String((item as Record<string, unknown>).id) === String(groupId))

      if (index !== -1) {
        const group = newData[index] as TData
        // Extract subrows to orphan them
        const subRows = ((group as Record<string, unknown>).subRows as TData[]) || []

        // Remove the group
        newData.splice(index, 1)

        // Add subrows to top-level
        // We can just push them or insert them where the group was.
        // "orphaned" usually means they become top-level rows.
        // Let's append them to the end to avoid index confusion or insert at the same place.
        // Appending to end is safer for now as seen in handleUngroup.
        if (subRows.length > 0) {
          newData.push(...subRows)
        }
      }
      return newData
    })
  }, [])

  // Handler for drop operations
  const handleDrop = useCallback(
    (draggedRowId: string, targetRowId: string) => {
      // Get the current expanded state from the table instance if available
      let wasExpanded = false
      if (tableRef.current) {
        const allRows = tableRef.current.getRowModel().rows
        const targetRow = allRows.find((row) => String(row.id) === String(targetRowId))
        if (targetRow) {
          wasExpanded = targetRow.getIsExpanded()
        }
      } else {
        // Fallback: check from expanded state ref
        const currentExpanded = expandedStateRef.current
        wasExpanded =
          currentExpanded[targetRowId] === true || (defaultExpanded && currentExpanded[targetRowId] !== false)
      }

      setLocalData((prevData) => {
        const newData = [...prevData]
        let draggedRow: TData | null = null
        let draggedRowIndex = -1
        let targetRow: TData | null = null
        let targetRowIndex = -1

        // Find both rows first (before any modifications)
        for (let i = 0; i < newData.length; i++) {
          const item = newData[i] as Record<string, unknown>
          const itemId = String(item.id || '')

          // Find dragged row (must be an orphan, not a group)
          if (itemId === String(draggedRowId) && !item.isGroup) {
            draggedRow = item as TData
            draggedRowIndex = i
          }

          // Find target row (can be orphan or group)
          if (itemId === String(targetRowId)) {
            targetRow = item as TData
            targetRowIndex = i
          }

          // Early exit if both found
          if (draggedRow && targetRow) break
        }

        if (!draggedRow || !targetRow) return prevData

        const targetItem = targetRow as Record<string, unknown>

        // Case 1: Drop on another orphan (create new group)
        if (!targetItem.isGroup) {
          // Remove both rows from top-level (remove higher index first to avoid index shifting issues)
          if (draggedRowIndex > targetRowIndex) {
            // Dragged row is below target - remove dragged first, then target
            newData.splice(draggedRowIndex, 1)
            newData.splice(targetRowIndex, 1)
          } else {
            // Dragged row is above target - remove target first, then dragged
            newData.splice(targetRowIndex, 1)
            newData.splice(draggedRowIndex, 1)
          }

          // Create a new group based on the target row
          // Generate a unique ID
          const newGroupId =
            typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

          const newGroup = {
            ...targetItem,
            id: newGroupId,
            subRows: [targetItem, draggedRow],
            isGroup: true,
            isExpanded: false, // Explicitly set to collapsed
          } as TData

          // Add new group to the end
          newData.push(newGroup)

          // New groups should be collapsed by default (don't add to expanded state)
          // Remove from expanded state if it was somehow there
          const currentExpanded = { ...expandedStateRef.current }
          delete currentExpanded[newGroupId]
          setExpandedState(currentExpanded)
          expandedStateRef.current = currentExpanded
        }
        // Case 2: Drop on an existing group (add to subRows)
        else if (targetItem.isGroup) {
          // Remove dragged row from top-level first
          newData.splice(draggedRowIndex, 1)

          // Find the target group again after removal (index may have shifted)
          let updatedTargetIndex = targetRowIndex
          if (draggedRowIndex < targetRowIndex) {
            // If dragged row was above target, target index shifts down by 1
            updatedTargetIndex = targetRowIndex - 1
          }

          // Get fresh reference to target group
          const updatedTargetGroup = newData[updatedTargetIndex] as Record<string, unknown>
          if (updatedTargetGroup && String(updatedTargetGroup.id) === String(targetRowId)) {
            const updatedGroup = {
              ...updatedTargetGroup,
              subRows: [...((updatedTargetGroup.subRows as TData[]) || []), draggedRow],
            }
            newData[updatedTargetIndex] = updatedGroup as TData

            // Preserve the expanded state we captured earlier
            const currentExpanded = { ...expandedStateRef.current }
            if (wasExpanded) {
              currentExpanded[targetRowId] = true
            } else {
              // Explicitly set to false to ensure collapsed
              currentExpanded[targetRowId] = false
            }
            setExpandedState(currentExpanded)
            expandedStateRef.current = currentExpanded
          }
        }

        return newData
      })
    },
    [setExpandedState, defaultExpanded, tableRef]
  )

  const handleResetGroups = useCallback(() => {
    setLocalData(groupedData)
  }, [groupedData])

  return {
    localData,
    setLocalData,
    expandedState,
    setExpandedState,
    handleExpandedChange,
    handleUngroup,
    handleDrop,
    handleUpdateGroup,
    handleDeleteGroup,
    handleResetGroups,
  }
}
